import { useEffect, useRef } from 'react';
import type { MetricsRecheckResponse } from '@discovr/features';
import { RowRecheckButton } from '../RowRecheckButton';
import { metricsRecheckLabel } from './creatorLookupStatus';
import { STALLED_PENDING_MS, type MetricsBaseline } from './creatorMetricsStatus';
import { DEFAULT_RECHECK_WINDOW_SECONDS, useProfileRecheckStore } from './profileRecheckStore';

const MS_PER_SECOND = 1_000;

type MetricsRecheckButtonProps = {
  campaignId: string;
  entryId: string;
  handle: string | undefined;
  metrics: MetricsBaseline;
  recheck: (entryId: string) => Promise<MetricsRecheckResponse>;
  /** Settling state, from the slot's own store subscription — kept here
   *  rather than a second useIsRechecking call for the same row + scope. */
  pending?: boolean;
};

/** Avg Views ⟳: queues the creator's reel-metrics job again. Spins until the
 *  row's metrics status / checked-at moves, bounded by the stall window. */
export function MetricsRecheckButton({ campaignId, entryId, handle, metrics, recheck, pending = false }: MetricsRecheckButtonProps) {
  const markRechecking = useProfileRecheckStore((s) => s.markRechecking);
  const clearRechecking = useProfileRecheckStore((s) => s.clearRechecking);

  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      clearRechecking(campaignId, entryId, 'metrics');
    };
  }, [campaignId, entryId, clearRechecking]);

  const handleRun = async () => {
    // Marked before the request, not after: an unmount while it is in flight
    // would otherwise run the cleanup first and let the late mark arm a timer
    // no component owns, holding a stale baseline for up to an hour. Also
    // shuts the double-click window. The server's own cooldown replaces this
    // provisional window once the 202 lands.
    markRechecking(campaignId, entryId, 'metrics', metrics, DEFAULT_RECHECK_WINDOW_SECONDS);
    try {
      const res = await recheck(entryId);
      // The row can unmount while the request is in flight; its cleanup has
      // already dropped the baseline by then, and re-marking would arm a timer
      // no component owns.
      // retry_after_seconds is the server COOLDOWN, not how long the job can
      // take — a longer-running job would expire the baseline mid-pending and
      // stop the spinner early, so the timer is the stall window (the store
      // clamps it; a non-numeric cooldown must not turn the max into NaN).
      const cooldown = Number.isFinite(res.retry_after_seconds) ? res.retry_after_seconds : 0;
      const windowSeconds = Math.max(cooldown, STALLED_PENDING_MS / MS_PER_SECOND);
      if (aliveRef.current) markRechecking(campaignId, entryId, 'metrics', metrics, windowSeconds);
    } catch (err) {
      clearRechecking(campaignId, entryId, 'metrics');
      throw err;
    }
  };

  return <RowRecheckButton label={metricsRecheckLabel(handle)} pending={pending} onRun={handleRun} />;
}
